import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { inviteUrl as buildInviteUrl } from '../links';

const W = { bg:'#0A1526', surface:'#16233F', border:'#28395C', muted:'#7d8ea2', bright:'#f1f5f9', accent:'#00A0C8', danger:'#fc8181' };
const inp = { padding:'9px 12px', borderRadius:'7px', border:`1px solid ${W.border}`, background:W.bg, color:W.bright, fontSize:'14px', width:'100%', boxSizing:'border-box', outline:'none' };
const lbl = { fontSize:'11px', color:W.muted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' };

const ROLE_CARDS = [
  { id:'member',          seat:'contractor',      title:'Contractor',          desc:'Can log time, view their own entries, and submit invoices. No access to reports, settings, or other team members.' },
  { id:'employee_hourly', seat:'employee_hourly', title:'Employee — Hourly',   desc:'Same access as Contractor. Compensation is calculated per hour logged.' },
  { id:'employee_salary', seat:'employee_salary', title:'Employee — Salary',   desc:'Same access as Contractor. Excluded from per-hour margin calculations.' },
  { id:'admin',           seat:'admin',           title:'Admin',               desc:'Full access to invoicing, client reports, team analytics, and settings.' },
];

const SEAT_LABELS = { contractor:'Contractor', admin:'Admin', employee_hourly:'Employee (Hourly)', employee_salary:'Employee (Salary)' };

export default function InviteTeamMemberWizard({ org, profile, onClose, onRefresh }) {
  const STEPS = ['Contact', 'Role', 'Confirm & Send'];
  const [step, setSte]   = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent]   = useState(false);
  const [err, setErr]     = useState('');
  const [invLink, setInvLink] = useState('');
  const [copied, setCopied]   = useState(false);
  const [sendEmail, setSendEmail] = useState(true);   // false = make the link only; the admin sends it themselves
  const [emailed, setEmailed]     = useState(true);   // whether the invitation just made was emailed

  const [email, setEmail]     = useState('');
  const [role, setRole]       = useState('member');
  const [seatType, setSeatType] = useState('contractor');

  const setStep = (v) => { setErr(''); setSte(v); };

  const selectedCard = ROLE_CARDS.find(c => c.id === (role === 'admin' ? 'admin' : seatType)) || ROLE_CARDS[0];

  const selectCard = (card) => {
    if (card.id === 'admin')           { setRole('admin');  setSeatType('admin'); }
    else if (card.id === 'employee_hourly') { setRole('member'); setSeatType('employee_hourly'); }
    else if (card.id === 'employee_salary') { setRole('member'); setSeatType('employee_salary'); }
    else                               { setRole('member'); setSeatType('contractor'); }
  };

  const canContinue = () => step === 0 ? email.trim().length > 0 && email.includes('@') : true;

  const send = async () => {
    setErr('');
    setSending(true);
    const token = Array.from(crypto.getRandomValues(new Uint8Array(32))).map(b => b.toString(16).padStart(2, '0')).join('');
    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    const dbRole = role === 'admin' ? 'admin' : 'member';
    const { error } = await supabase.from('pending_invitations').insert({
      org_id: org.id, email: email.trim(), role: dbRole,
      seat_type: seatType, token, expires_at: expiresAt, invited_by: profile.id, status: 'pending',
    });
    setSending(false);
    if (error) { setErr(error.message); return; }
    const url = buildInviteUrl(token);
    setInvLink(url);
    setEmailed(sendEmail);
    if (sendEmail) {
      supabase.functions.invoke('send-invitation', {
        body: { invitee_email: email.trim(), org_id: org.id, org_name: org.name, role: dbRole, seat_type: seatType, invite_url: url },
      }).catch(console.error);
    }
    onRefresh();
    setSent(true);
  };

  const copyLink = () => { navigator.clipboard.writeText(invLink); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const stepContent = [
    <div key="contact">
      <div style={{ marginBottom:'20px' }}>
        <div style={lbl}>Email address</div>
        <input style={inp} type="email" placeholder="name@example.com" value={email} autoFocus
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && canContinue() && setStep(1)} />
        <div style={{ fontSize:'11px', color:W.muted, marginTop:'6px' }}>They must sign up using this same email.</div>
      </div>
    </div>,

    <div key="role">
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        {ROLE_CARDS.map(card => {
          const active = selectedCard.id === card.id;
          return (
            <div key={card.id} onClick={() => selectCard(card)}
              style={{ padding:'14px 16px', borderRadius:'9px', cursor:'pointer', transition:'border-color 0.15s,background 0.15s',
                border:`1px solid ${active ? W.accent : W.border}`,
                background: active ? 'rgba(0,160,200,0.08)' : W.bg }}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <div style={{ width:'16px', height:'16px', borderRadius:'50%', flexShrink:0, transition:'all 0.15s',
                  border:`2px solid ${active ? W.accent : '#3a4558'}`,
                  background: active ? W.accent : 'transparent' }} />
                <div>
                  <div style={{ fontSize:'14px', fontWeight:'700', color:W.bright }}>{card.title}</div>
                  <div style={{ fontSize:'12px', color:W.muted, marginTop:'3px', lineHeight:'1.4' }}>{card.desc}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>,

    <div key="confirm">
      <div style={{ padding:'16px', borderRadius:'9px', border:`1px solid ${W.border}`, background:W.bg, marginBottom:'16px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
          <div><div style={lbl}>Email</div><div style={{ fontSize:'13px', color:W.bright, wordBreak:'break-all' }}>{email}</div></div>
          <div><div style={lbl}>Role</div><div style={{ fontSize:'13px', color:W.bright }}>{SEAT_LABELS[seatType]}</div></div>
        </div>
      </div>
      <label style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer', marginBottom:'10px', fontSize:'13px', color:W.bright }}>
        <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} style={{ accentColor:W.accent }} />
        Email them the invitation
      </label>
      <div style={{ fontSize:'12px', color:W.muted, lineHeight:'1.5' }}>
        {sendEmail
          ? 'An invitation email will be sent immediately. '
          : 'Nothing will be emailed. You will get a link to send them yourself. '}
        The link expires in 7 days. You can revoke it anytime from the Team page.
      </div>
      {err && <div style={{ fontSize:'12px', color:W.danger, marginTop:'10px' }}>{err}</div>}
    </div>,
  ];

  const successContent = (
    <div style={{ textAlign:'center', padding:'12px 0' }}>
      <div style={{ fontSize:'36px', marginBottom:'12px' }}>{emailed ? '✉️' : '🔗'}</div>
      <div style={{ fontSize:'16px', fontWeight:'700', color:W.bright, marginBottom:'6px' }}>{emailed ? 'Invitation sent!' : 'Invite link ready'}</div>
      <div style={{ fontSize:'13px', color:W.muted, marginBottom:'20px' }}>
        {emailed
          ? <>An email was sent to <strong style={{ color:W.bright }}>{email}</strong>. Share the link below if they don't receive it.</>
          : <>Nothing was emailed. Copy the link below and send it to <strong style={{ color:W.bright }}>{email}</strong> yourself.</>}
      </div>
      <div style={{ padding:'12px', background:'rgba(0,160,200,0.06)', border:'1px solid rgba(0,160,200,0.2)', borderRadius:'8px', marginBottom:'16px' }}>
        <div style={{ fontSize:'11px', color:W.muted, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Invite link — expires in 7 days</div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <input readOnly value={invLink} style={{ ...inp, flex:1, fontSize:'11px', color:'#94a3b8' }} />
          <button onClick={copyLink}
            style={{ padding:'8px 14px', borderRadius:'7px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'13px', flexShrink:0, transition:'background 0.15s',
              background: copied ? W.border : W.accent, color: copied ? W.muted : '#fff' }}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>
      <div style={{ display:'flex', gap:'10px', justifyContent:'center' }}>
        <button onClick={() => { setEmail(''); setRole('member'); setSeatType('contractor'); setInvLink(''); setSent(false); setStep(0); }}
          style={{ padding:'8px 18px', borderRadius:'7px', border:`1px solid ${W.border}`, background:'transparent', color:W.bright, fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>
          Invite Another
        </button>
        <button onClick={onClose}
          style={{ padding:'8px 18px', borderRadius:'7px', border:'none', background:W.accent, color:'#fff', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>
          Done
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200, padding:'20px' }}>
      <div style={{ background:W.surface, border:`1px solid ${W.border}`, borderRadius:'14px', width:'100%', maxWidth:'480px', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ padding:'22px 24px 16px', borderBottom:`1px solid ${W.border}`, flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontSize:'17px', fontWeight:'800', color:W.bright }}>Invite Team Member</div>
              {!sent && <div style={{ fontSize:'12px', color:W.muted, marginTop:'2px' }}>Step {step + 1} of {STEPS.length} — {STEPS[step]}</div>}
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', color:W.muted, fontSize:'20px', cursor:'pointer', lineHeight:1, padding:'2px' }}>×</button>
          </div>
          {!sent && (
            <div style={{ marginTop:'14px', height:'4px', borderRadius:'2px', background:W.border, overflow:'hidden' }}>
              <div style={{ height:'100%', borderRadius:'2px', background:W.accent, width:`${((step + 1) / STEPS.length) * 100}%`, transition:'width 0.3s' }} />
            </div>
          )}
        </div>

        <div style={{ padding:'22px 24px', overflowY:'auto', flex:1 }}>
          {sent ? successContent : stepContent[step]}
        </div>

        {!sent && (
          <div style={{ padding:'16px 24px', borderTop:`1px solid ${W.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
            {step > 0
              ? <button onClick={() => setStep(step - 1)} style={{ padding:'8px 18px', borderRadius:'7px', border:`1px solid ${W.border}`, background:'transparent', color:W.bright, fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>← Back</button>
              : <div />
            }
            {step < STEPS.length - 1
              ? <button onClick={() => canContinue() && setStep(step + 1)} disabled={!canContinue()}
                  style={{ padding:'8px 22px', borderRadius:'7px', border:'none', fontSize:'13px', fontWeight:'600', transition:'background 0.15s',
                    background: canContinue() ? W.accent : W.border, color: canContinue() ? '#fff' : W.muted, cursor: canContinue() ? 'pointer' : 'default' }}>
                  Continue →
                </button>
              : <button onClick={send} disabled={sending}
                  style={{ padding:'8px 22px', borderRadius:'7px', border:'none', fontSize:'13px', fontWeight:'600',
                    background: sending ? W.border : W.accent, color: sending ? W.muted : '#fff', cursor: sending ? 'default' : 'pointer' }}>
                  {sending ? 'Working…' : (sendEmail ? 'Send Invitation' : 'Create invite link')}
                </button>
            }
          </div>
        )}
      </div>
    </div>
  );
}
